/**
 * Flattening a cell comment thread into one plain-text Excel cell note.
 *
 * Shared by both Item Master review exports (the review workbook's cell notes
 * and the Review-mode workbook), so a thread reads the same in either file.
 */

import { commentBodyToPlainText } from "./comment-mentions";

/** The parts of a comment thread a note shows — `ChangeSetCommentThread` fits. */
export interface CommentNoteThread {
  resolvedAt: Date | string | null;
  /** Oldest first. */
  messages: ReadonlyArray<{
    authorName: string | null;
    createdAt: Date | string;
    body: string;
  }>;
}

/**
 * What a note writes after the `@` of a `{user:<uuid>}` mention, by user id;
 * undefined for a user it can't name, who reads as `@Unknown user`. The
 * caller looks every name up beforehand, so formatting stays pure.
 */
export type MentionLabel = (userId: string) => string | undefined;

const NO_MENTION_LABELS: MentionLabel = () => undefined;

export const UNKNOWN_COMMENT_AUTHOR = "Unknown";
export const RESOLVED_COMMENT_NOTE_SUFFIX = "(resolved)";

/** `YYYY-MM-DD` in UTC, so the server and every browser print the same day. */
function noteDate(value: Date | string): string {
  return new Date(value).toISOString().slice(0, 10);
}

/**
 * One line per message, `Author (YYYY-MM-DD): text`, oldest first, then
 * `(resolved)` on its own line when the thread is resolved. A message's own
 * line breaks are kept, and its @mentions read `@` + `mentionLabel`, never a
 * raw user id.
 */
export function formatThreadAsNote(
  thread: CommentNoteThread,
  mentionLabel: MentionLabel = NO_MENTION_LABELS,
): string {
  const lines = thread.messages.map(
    (message) =>
      `${message.authorName ?? UNKNOWN_COMMENT_AUTHOR} (${noteDate(message.createdAt)}): ${commentBodyToPlainText(message.body, mentionLabel)}`,
  );
  if (thread.resolvedAt !== null) lines.push(RESOLVED_COMMENT_NOTE_SUFFIX);
  return lines.join("\n");
}

/**
 * The most text one Excel cell value or cell note holds. Past it, Excel cuts
 * the text or offers to repair the workbook.
 */
export const EXCEL_CELL_TEXT_MAX_CHARS = 32_767;

function truncationMarker(droppedChars: number): string {
  return `… [truncated: ${droppedChars} more characters, see the review in the app]`;
}

/**
 * `text` as it fits in one Excel cell or note: unchanged when it fits, else cut
 * and ended with a marker saying how much was left out, the two together
 * within {@link EXCEL_CELL_TEXT_MAX_CHARS}. The cut never splits a surrogate
 * pair. Apply it to a cell's final text, after every thread is joined.
 */
export function capExcelCellText(text: string): string {
  if (text.length <= EXCEL_CELL_TEXT_MAX_CHARS) return text;
  // Sized for the most that could be dropped; the real count has no more digits.
  let kept = EXCEL_CELL_TEXT_MAX_CHARS - truncationMarker(text.length).length;
  const last = text.charCodeAt(kept - 1);
  if (last >= 0xd800 && last <= 0xdbff) kept -= 1;
  return text.slice(0, kept) + truncationMarker(text.length - kept);
}
